import React, { useEffect, useMemo, useState } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import EditSquareIcon from "@/icons/afrimed/edit_square.svg";
import NavigateBeforeIcon from "@/icons/afrimed/navigate_before.svg";
import NavigateNextIcon from "@/icons/afrimed/navigate_next.svg";
import ShowLessIcon from "@/icons/afrimed/Show_less.svg";
import { cn } from "../../utils/cn";

function validDate(value) {
  return value instanceof Date && !Number.isNaN(value.getTime()) ? value : null;
}

function safeText(value, fallback) {
  if (typeof value !== "string") return fallback;
  if (!value.trim() || /^common\./.test(value)) return fallback;
  return value;
}

function safeTranslate(t, key, fallback) {
  return safeText(t?.(key, fallback), fallback);
}

function displayDate(date, fallback = "Select date") {
  const safeDate = validDate(date);
  if (!safeDate) return fallback;
  return format(safeDate, "EEE, MMM d");
}

function AfriMedIcon({ src, className = "", label }) {
  return (
    <img
      src={src}
      alt={label || ""}
      aria-hidden={label ? undefined : "true"}
      className={cn("block h-6 w-6 object-contain", className)}
      draggable="false"
    />
  );
}

const DatePicker = ({
  value,
  onChange,
  mode = "single",
  className,
  navButtonClassName = "",
  onCancel,
  onConfirm,
  commitOnSelect = true,
  minDate,
  maxDate,
  anchorRef,
  dismissOnOutside = false,
  dismissOnEscape = false,
  returnFocusOnDismiss = false,
}) => {
  const { t } = useLanguage();
  const rootRef = React.useRef(null);
  const isRange = mode === "range";
  const [draftValue, setDraftValue] = useState(value);
  const startDate = validDate(isRange ? draftValue?.start : draftValue);
  const endDate = validDate(isRange ? draftValue?.end : null);
  const activeDate = startDate || endDate || null;

  const [currentMonth, setCurrentMonth] = useState(() => activeDate || new Date());
  const [monthPanelOpen, setMonthPanelOpen] = useState(false);
  const [yearPanelOpen, setYearPanelOpen] = useState(false);

  useEffect(() => {
    setDraftValue(value);
    const nextActive = validDate(isRange ? value?.start : value) || validDate(isRange ? value?.end : null);
    if (nextActive) setCurrentMonth(nextActive);
  }, [isRange, value]);

  const dismiss = React.useCallback(
    (reason) => {
      onCancel?.(reason);
      if (returnFocusOnDismiss) {
        window.requestAnimationFrame(() => anchorRef?.current?.focus?.());
      }
    },
    [anchorRef, onCancel, returnFocusOnDismiss],
  );

  useEffect(() => {
    if (!dismissOnOutside && !dismissOnEscape) return undefined;

    const handlePointerDown = (event) => {
      if (!dismissOnOutside) return;
      const target = event.target;
      if (rootRef.current?.contains(target) || anchorRef?.current?.contains?.(target)) return;
      dismiss("outside");
    };
    const handleKeyDown = (event) => {
      if (!dismissOnEscape || event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      dismiss("escape");
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [anchorRef, dismiss, dismissOnEscape, dismissOnOutside]);

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, idx) => ({
        value: idx,
        label: format(new Date(2000, idx, 1), "MMMM"),
      })),
    [],
  );

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const years = [];
    for (let y = now + 10; y >= now - 120; y -= 1) {
      years.push({ value: y, label: String(y) });
    }
    return years;
  }, []);

  const weekdayLabels = useMemo(
    () => [
      safeTranslate(t, "common.datePicker.weekdays.sunday", "S"),
      safeTranslate(t, "common.datePicker.weekdays.monday", "M"),
      safeTranslate(t, "common.datePicker.weekdays.tuesday", "T"),
      safeTranslate(t, "common.datePicker.weekdays.wednesday", "W"),
      safeTranslate(t, "common.datePicker.weekdays.thursday", "T"),
      safeTranslate(t, "common.datePicker.weekdays.friday", "F"),
      safeTranslate(t, "common.datePicker.weekdays.saturday", "S"),
    ],
    [t],
  );

  const handleDateSelect = (date) => {
    if (validDate(minDate) && isBefore(date, minDate)) return;
    if (validDate(maxDate) && isBefore(maxDate, date)) return;
    if (isRange) {
      let nextValue;
      if (!startDate || (startDate && endDate)) {
        nextValue = { start: date, end: null };
      } else if (isBefore(date, startDate)) {
        nextValue = { start: date, end: startDate };
      } else {
        nextValue = { start: startDate, end: date };
      }
      setDraftValue(nextValue);
      if (commitOnSelect) onChange?.(nextValue);
      return;
    }
    setDraftValue(date);
    if (commitOnSelect) onChange?.(date);
  };

  const commitDraft = () => {
    const nextValue = isRange ? { start: startDate, end: endDate } : startDate;
    if (typeof onConfirm === "function") {
      onConfirm(nextValue);
      return;
    }
    onChange?.(nextValue);
  };

  const selectMonth = (nextMonth) => {
    const next = new Date(currentMonth);
    next.setMonth(Number(nextMonth), 1);
    setCurrentMonth(next);
    setMonthPanelOpen(false);
  };

  const selectYear = (nextYear) => {
    const next = new Date(currentMonth);
    next.setFullYear(Number(nextYear), currentMonth.getMonth(), 1);
    setCurrentMonth(next);
    setYearPanelOpen(false);
  };

  const visibleDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(currentMonth)),
    end: endOfWeek(endOfMonth(currentMonth)),
  });

  const monthLabel = `${monthOptions[currentMonth.getMonth()]?.label || format(currentMonth, "MMMM")} ${currentMonth.getFullYear()}`;
  const headline = isRange && startDate && endDate
    ? `${displayDate(startDate)} - ${displayDate(endDate)}`
    : displayDate(activeDate);

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full max-w-[360px] overflow-hidden rounded-[28px] bg-[rgb(var(--clr-surface-cont-highest))] text-on-surface",
        className,
      )}
      style={{
        boxShadow:
          "0 0 0 1px rgba(24,50,119,0.10), 0 1.5px 0 -0.5px rgba(26,27,35,0.06), 0 3px 3px -1px rgba(26,27,35,0.06), 0 3px 6px -2px rgba(26,27,35,0.06), 0 6px 12px -4px rgba(29,79,215,0.06), 0 12px 24px -10px rgba(29,79,215,0.06), 0 12px 36px -6px rgba(26,27,35,0.06)",
      }}
    >
      <div className="flex items-end gap-2 border-b border-[rgb(var(--clr-outline-variant))] pb-3 pl-6 pr-3 pt-4">
        <div className="min-w-0 flex-1">
          <p className="text-label-large text-on-surface-var">
            {safeTranslate(t, "common.datePicker.selectDate", "Select date")}
          </p>
          <p className="mt-9 truncate text-title-large font-normal text-on-surface">
            {headline}
          </p>
        </div>
        <button
          type="button"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-[12px] text-on-surface-var transition-colors hover:bg-[rgba(var(--clr-on-surface),0.06)]"
          aria-label={safeTranslate(t, "common.datePicker.editDate", "Edit date")}
          aria-expanded={yearPanelOpen}
          style={{ touchAction: "manipulation" }}
          onClick={() => {
            setYearPanelOpen((open) => !open);
            setMonthPanelOpen(false);
          }}
        >
          <AfriMedIcon src={EditSquareIcon} />
        </button>
      </div>

      <div className="flex items-center justify-between py-1 pl-4 pr-3">
        <button
          type="button"
          onClick={() => {
            setMonthPanelOpen((open) => !open);
            setYearPanelOpen(false);
          }}
          className="inline-flex min-w-0 items-center gap-2 rounded-[12px] py-2.5 pl-2 pr-1 text-label-large text-on-surface-var transition-colors hover:bg-[rgba(var(--clr-on-surface),0.06)]"
          aria-expanded={monthPanelOpen}
          aria-label={safeTranslate(t, "common.datePicker.selectMonth", "Select month")}
        >
          <span className="truncate">{monthLabel}</span>
          <AfriMedIcon src={ShowLessIcon} className={cn("h-[18px] w-[18px] transition-transform", monthPanelOpen && "rotate-180")} />
        </button>

        <div className="flex shrink-0 items-center">
          <button
            type="button"
            className={cn(
              "grid h-11 w-11 place-items-center rounded-[12px] text-on-surface-var transition-colors hover:bg-[rgba(var(--clr-on-surface),0.06)]",
              navButtonClassName,
            )}
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            aria-label={safeTranslate(t, "common.datePicker.previousMonth", "Previous month")}
          >
            <AfriMedIcon src={NavigateBeforeIcon} />
          </button>
          <button
            type="button"
            className={cn(
              "grid h-11 w-11 place-items-center rounded-[12px] text-on-surface-var transition-colors hover:bg-[rgba(var(--clr-on-surface),0.06)]",
              navButtonClassName,
            )}
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            aria-label={safeTranslate(t, "common.datePicker.nextMonth", "Next month")}
          >
            <AfriMedIcon src={NavigateNextIcon} />
          </button>
        </div>
      </div>

      {monthPanelOpen && (
        <div className="mx-3 mb-2 rounded-[16px] border border-[rgb(var(--clr-outline-variant))] bg-[rgb(var(--clr-surface-cont-highest))] p-2">
          <div className="grid grid-cols-3 gap-1">
            {monthOptions.map((month) => {
              const active = Number(month.value) === currentMonth.getMonth();
              return (
                <button
                  key={month.value}
                  type="button"
                  onClick={() => selectMonth(month.value)}
                  className={cn(
                    "h-10 rounded-[10px] px-2 text-left text-label-medium transition-colors",
                    active ? "bg-[var(--state-primary-12)] text-primary" : "text-on-surface hover:bg-[rgba(var(--clr-on-surface),0.05)]",
                  )}
                >
                  {month.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              setYearPanelOpen((open) => !open);
              setMonthPanelOpen(false);
            }}
            className="mt-2 h-10 w-full rounded-[10px] text-label-large text-primary hover:bg-[var(--state-primary-12)]"
          >
            {safeTranslate(t, "common.datePicker.changeYear", "Change year")}
          </button>
        </div>
      )}

      {yearPanelOpen && (
        <div className="mx-3 mb-2 rounded-[16px] border border-[rgb(var(--clr-outline-variant))] bg-[rgb(var(--clr-surface-cont-highest))] p-2">
          <div className="grid max-h-[240px] grid-cols-4 gap-1 overflow-y-auto pr-1">
            {yearOptions.map((year) => {
              const active = Number(year.value) === currentMonth.getFullYear();
              return (
                <button
                  key={year.value}
                  type="button"
                  onClick={() => selectYear(year.value)}
                  className={cn(
                    "h-10 rounded-[10px] text-center text-label-medium transition-colors",
                    active ? "bg-[var(--state-primary-12)] text-primary" : "text-on-surface hover:bg-[rgba(var(--clr-on-surface),0.05)]",
                  )}
                >
                  {year.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-3">
        <div className="grid h-8 grid-cols-7">
          {weekdayLabels.map((day, index) => (
            <div key={`${day}-${index}`} className="flex items-center justify-center text-body-large text-on-surface-var opacity-60">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {visibleDays.map((day) => {
            const isSelected = (startDate && isSameDay(day, startDate)) || (endDate && isSameDay(day, endDate));
            const inRange = isRange && startDate && endDate && isWithinInterval(day, { start: startDate, end: endDate });
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isDisabled = (validDate(minDate) && isBefore(day, minDate)) || (validDate(maxDate) && isBefore(maxDate, day));

            return (
              <button
                key={day.toISOString()}
                type="button"
                disabled={isDisabled}
                onClick={() => handleDateSelect(day)}
                className="flex h-12 items-center justify-center disabled:cursor-not-allowed"
                aria-pressed={!!isSelected}
              >
                <span
                  className={cn(
                    "grid h-[30px] w-[30px] place-items-center rounded-[8px] text-title-medium transition-colors",
                    !isCurrentMonth && "text-[rgba(var(--clr-on-surface-variant),0.45)]",
                    isCurrentMonth && "text-on-surface",
                    isSelected && "bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))]",
                    inRange && !isSelected && "bg-[var(--state-primary-12)] text-primary",
                    isToday(day) && !isSelected && "text-[rgb(var(--schemes-secondary))]",
                    isDisabled && "text-[rgba(var(--clr-on-surface-variant),0.35)]",
                  )}
                >
                  {format(day, "d")}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-2 px-3 pb-3 pt-2">
        <button
          type="button"
          className="rounded-[12px] px-3 py-3 text-label-large text-primary transition-colors hover:bg-[var(--state-primary-12)]"
          style={{ touchAction: "manipulation" }}
          onClick={() => {
            setDraftValue(value);
            dismiss("cancel");
          }}
        >
          {safeTranslate(t, "common.cancel", "Cancel")}
        </button>
        <button
          type="button"
          className="rounded-[12px] px-3 py-3 text-label-large text-primary transition-colors hover:bg-[var(--state-primary-12)]"
          style={{ touchAction: "manipulation" }}
          onClick={commitDraft}
        >
          {safeTranslate(t, "common.ok", "OK")}
        </button>
      </div>
    </div>
  );
};

export default DatePicker;
